import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { DollarSign, FileText, Layers, MapPin, PiggyBank, Search, Settings2, Tags, Users, X } from 'lucide-react';
import { DNA } from 'react-loader-spinner';
import AnalyticsCard from '../components/Analytics/AnalyticsCard';
import SideSheet from '../components/Analytics/SideSheet';
import TopBilledCodesBody from '../components/Analytics/TopBilledCodesBody';
import TopBilledGroupsBody from '../components/Analytics/TopBilledGroupsBody';
import TopBilledMembersBody from '../components/Analytics/TopBilledMembersBody';
import TopBilledStatesBody from '../components/Analytics/TopBilledStatesBody';
import { InputTextField } from '../components/Input/InputTextField';
import USAMap from '../components/Map/USAMap';
import { useData } from '../hooks/useData';
import { useOrganization } from '../hooks/useOrganization';
import {
  createBillingProviderGeoCache,
  getAnalyticsFiles,
  geocodeBillingProviderMapPoints,
  getBillingProviderMapPoints,
  getAnalyticsOverview,
  getLatestRepricedFiles,
  getServiceLineMapSummary
} from '../utils/analytics';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [visibleOverviewCards, setVisibleOverviewCards] = useState({
    totalClaims: true,
    totalBilled: true,
    totalSavings: true,
    topBilledCodes: true,
    topBilledMembers: true,
    topBilledStates: true,
    topBilledGroups: true
  });
  const [draftVisibleOverviewCards, setDraftVisibleOverviewCards] = useState({
    totalClaims: true,
    totalBilled: true,
    totalSavings: true,
    topBilledCodes: true,
    topBilledMembers: true,
    topBilledStates: true,
    topBilledGroups: true
  });
  const { accountSelected } = useOrganization();
  const { files, getFilesByOrg } = useData();
  const [geocodedAllBillingProviderMapPoints, setGeocodedAllBillingProviderMapPoints] = useState([]);
  const [geocodedBillingProviderMapPoints, setGeocodedBillingProviderMapPoints] = useState([]);
  const [mapServiceLineInput, setMapServiceLineInput] = useState('');
  const [mapServiceLineQuery, setMapServiceLineQuery] = useState('');
  const [isMapSearchLoading, setIsMapSearchLoading] = useState(false);
  const getFilesByOrgRef = useRef(getFilesByOrg);
  const hasStartedMapGeocodingRef = useRef(false);

  getFilesByOrgRef.current = getFilesByOrg;

  useEffect(() => {
    if (accountSelected) {
      getFilesByOrgRef.current();
    }
  }, [accountSelected]);

  const repricedFiles = useMemo(() => {
    return getAnalyticsFiles(files).reduce((latestRepricedFiles, file) => {
      const latestRepricedFile = getLatestRepricedFiles([file])[0] || null;

      if (latestRepricedFile) {
        latestRepricedFiles.push(latestRepricedFile);
      }

      return latestRepricedFiles;
    }, []);
  }, [files]);

  const { overviewCards } = useMemo(() => getAnalyticsOverview({
    files,
    repricedFiles,
    icons: {
      totalClaims: <FileText size={22} strokeWidth={2.4} />,
      totalBilled: <DollarSign size={22} strokeWidth={2.6} />,
      totalSavings: <PiggyBank size={22} strokeWidth={2.4} />,
      topBilledCodes: <Tags size={22} strokeWidth={2.4} />,
      topBilledMembers: <Users size={22} strokeWidth={2.4} />,
      topBilledStates: <MapPin size={22} strokeWidth={2.4} />,
      topBilledGroups: <Layers size={22} strokeWidth={2.4} />
    },
    renderTopBilledCodesBody: (codes) => <TopBilledCodesBody codes={codes} />,
    renderTopBilledMembersBody: (members) => <TopBilledMembersBody members={members} />,
    renderTopBilledStatesBody: (states) => <TopBilledStatesBody states={states} />,
    renderTopBilledGroupsBody: (groups) => <TopBilledGroupsBody groups={groups} />
  }), [files, repricedFiles]);

  const allBillingProviderMapPoints = useMemo(() => {
    return getBillingProviderMapPoints(files);
  }, [files]);

  const allBillingProviderGeoPoints = geocodedAllBillingProviderMapPoints.length > 0
    ? geocodedAllBillingProviderMapPoints
    : allBillingProviderMapPoints;

  const billingProviderGeoCache = useMemo(() => {
    return createBillingProviderGeoCache(allBillingProviderGeoPoints);
  }, [allBillingProviderGeoPoints]);

  const billingProviderMapPoints = useMemo(() => {
    if (!mapServiceLineQuery.trim()) {
      return allBillingProviderGeoPoints;
    }

    return getBillingProviderMapPoints(files, {
      serviceLineQuery: mapServiceLineQuery,
      geoCache: billingProviderGeoCache
    });
  }, [allBillingProviderGeoPoints, billingProviderGeoCache, files, mapServiceLineQuery]);

  const mapSummaryItems = useMemo(() => {
    const serviceLineSummaryItems = getServiceLineMapSummary(files, mapServiceLineQuery, repricedFiles);

    if (serviceLineSummaryItems) {
      return serviceLineSummaryItems;
    }

    return ['totalClaims', 'totalBilled', 'totalSavings']
      .map((cardId) => overviewCards.find((card) => card.id === cardId))
      .filter(Boolean);
  }, [files, mapServiceLineQuery, overviewCards, repricedFiles]);

  useEffect(() => {
    let isMounted = true;

    setGeocodedAllBillingProviderMapPoints(allBillingProviderMapPoints);

    if (activeTab !== 'maps') {
      return () => {
        isMounted = false;
      };
    }

    if (allBillingProviderMapPoints.length === 0) {
      return () => {
        isMounted = false;
      };
    }

    if (hasStartedMapGeocodingRef.current) {
      return () => {
        isMounted = false;
      };
    }

    hasStartedMapGeocodingRef.current = true;

    const loadGeocodedPoints = async () => {
      const geocodedPoints = await geocodeBillingProviderMapPoints(allBillingProviderMapPoints);

      if (isMounted) {
        setGeocodedAllBillingProviderMapPoints(geocodedPoints);
      }
    };

    loadGeocodedPoints();

    return () => {
      isMounted = false;
    };
  }, [activeTab, allBillingProviderMapPoints]);

  useEffect(() => {
    let isMounted = true;

    setGeocodedBillingProviderMapPoints(billingProviderMapPoints);

    requestAnimationFrame(() => {
      if (isMounted) {
        setIsMapSearchLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [billingProviderMapPoints]);

  const handleOverviewCardToggle = (cardId) => {
    setDraftVisibleOverviewCards((currentCards) => ({
      ...currentCards,
      [cardId]: !currentCards[cardId]
    }));
  };

  const handleOverviewSelectAllToggle = (checked) => {
    setDraftVisibleOverviewCards((currentCards) => {
      return overviewCards.reduce((nextCards, card) => ({
        ...nextCards,
        [card.id]: checked
      }), currentCards);
    });
  };

  const handleCustomizeOpen = () => {
    setDraftVisibleOverviewCards(visibleOverviewCards);
    setIsCustomizeOpen(true);
  };

  const handleCustomizeClose = () => {
    setDraftVisibleOverviewCards(visibleOverviewCards);
    setIsCustomizeOpen(false);
  };

  const handleCustomizeSave = () => {
    setVisibleOverviewCards(draftVisibleOverviewCards);
    setIsCustomizeOpen(false);
  };

  const handleMapServiceLineSearch = (event) => {
    event.preventDefault();
    const nextServiceLineQuery = mapServiceLineInput.trim();

    if (nextServiceLineQuery === mapServiceLineQuery.trim()) {
      return;
    }

    setIsMapSearchLoading(true);
    React.startTransition(() => {
      setMapServiceLineQuery(nextServiceLineQuery);
    });
  };

  const handleMapServiceLineClear = () => {
    setMapServiceLineInput('');
    setIsMapSearchLoading(false);
    React.startTransition(() => {
      setMapServiceLineQuery('');
    });
  };

  useEffect(() => {
    if (!isCustomizeOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDraftVisibleOverviewCards(visibleOverviewCards);
        setIsCustomizeOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isCustomizeOpen, visibleOverviewCards]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      setIsCustomizeOpen(false);
    }
  }, [activeTab]);

  if (!accountSelected) {
    return (
      <div className="main-content page-analytics">
        <p style={{ textAlign: 'center', marginTop: 50, fontSize: '18px', fontWeight: 'bold' }}>
          Select account to view analytics
        </p>
      </div>
    );
  }

  return (
    <div className="main-content page-analytics">
      <div className="analytics-page-toolbar">
        <div className="bubble-tabs" role="tablist" aria-label="Analytics views">
          <Button
            variant="text"
            onClick={() => setActiveTab('overview')}
            role="tab"
            aria-selected={activeTab === 'overview'}
            className={`bubble-tab${activeTab === 'overview' ? ' bubble-tab--active' : ''}`}
          >
            Overview
          </Button>
          <Button
            variant="text"
            onClick={() => setActiveTab('maps')}
            role="tab"
            aria-selected={activeTab === 'maps'}
            className={`bubble-tab${activeTab === 'maps' ? ' bubble-tab--active' : ''}`}
          >
            Maps
          </Button>
        </div>

        {activeTab === 'overview' && (
          <Tooltip title="Customize overview">
            <IconButton
              aria-label="Customize analytics overview"
              className="analytics-customize-button"
              onClick={handleCustomizeOpen}
            >
              <Settings2 size={22} strokeWidth={2.2} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className="analytics-tab-panel" role="tabpanel">
          <div className="analytics-overview-grid">
            {overviewCards
              .filter((card) => visibleOverviewCards[card.id])
              .map((card) => (
                <AnalyticsCard key={card.id} title={card.title} body={card.body} icon={card.icon} />
              ))}
          </div>
        </div>
      )}

      {activeTab === 'maps' && (
        <div className="analytics-tab-panel" role="tabpanel">
          <div className="analytics-map-summary" aria-label="Map totals">
            {mapSummaryItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <span className="analytics-map-summary__separator">|</span>}
                <span className="analytics-map-summary__item">
                  <span className="analytics-map-summary__label">{item.title}:</span>
                  <span className="analytics-map-summary__value">{item.body}</span>
                </span>
              </React.Fragment>
            ))}
          </div>

          <form className="analytics-map-search" onSubmit={handleMapServiceLineSearch}>
            <div className="analytics-map-search__input">
              <InputTextField
                value={mapServiceLineInput}
                onChange={setMapServiceLineInput}
                placeholder="Search service lines"
                endIcon={<Search size={16} />}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              className="analytics-map-search__button"
              startIcon={<Search size={16} />}
            >
              Search
            </Button>
            <Button
              type="button"
              variant="outlined"
              className="analytics-map-search__clear"
              startIcon={<X size={16} />}
              onClick={handleMapServiceLineClear}
            >
              Clear
            </Button>
          </form>

          <div className="analytics-map-canvas">
            <USAMap
              locationData={geocodedBillingProviderMapPoints}
              loading={isMapSearchLoading}
              showProviderLabels={Boolean(mapServiceLineQuery.trim())}
              specification={{
                color: '#48a99a',
                size: 5,
                hoverSize: 5,
                fillOpacity: 0.65,
                hoverFillOpacity: 0.65,
                strokeOpacity: 0.8,
                strokeWidth: 1
              }}
            />
            {isMapSearchLoading && (
              <div className="analytics-map-loader" aria-label="Loading service line map">
                <DNA height="44" width="44" color="#000000" ariaLabel="dna-loading" wrapperStyle={{}} wrapperClass="dna-wrapper" />
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'overview' && isCustomizeOpen && (
        <SideSheet
          overviewCards={overviewCards}
          draftVisibleOverviewCards={draftVisibleOverviewCards}
          onClose={handleCustomizeClose}
          onSave={handleCustomizeSave}
          onToggleCard={handleOverviewCardToggle}
          onToggleSelectAll={handleOverviewSelectAllToggle}
        />
      )}
    </div>
  );
};

export default Analytics; 
